import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './common/auth.guard';
import { LoginComponent } from './web/login/login.component';
import { PrivacyPolicyComponent } from './web/privacy-policy/privacy-policy.component';
import { TermsofuseComponent } from './web/termsofuse/termsofuse.component';
import { BarCodeComponent } from './web/leftnav/barcode/bar-code.component';
import { WaterQualityHistoryComponent } from './web/leftnav/Water-quality-Readings-History/water-quality-history.component';
import { WaterQualityComponent } from './web/leftnav/Water-quality-metrics/water-quality.component';
import { BatchEntryDashboardComponent } from './web/leftnav/BatchTransactions/batch-entry-dashboard/batch-entry-dashboard.component';
import { PondTracebilityComponent } from './web/leftnav/Traceability-Report/pond-tracebility.component';
import { AnalyticsComponent } from './web/leftnav/Charts/analytics/analytics.component';
import { HomeComponent } from './home/home.component';
import { MapDeviceComponent } from './web/leftnav/map-device/map-device.component';
import { LandingpageComponent } from './web/landing-page/landing-page.component';
import { SectionsComponent } from './web/leftnav/sections/sections.component';
import { PondsComponent } from './web/leftnav/ponds/ponds.component';
import { PondCulturesComponent } from './web/leftnav/pond-cultures/pond-cultures.component';
import { LocationComponent } from './web/leftnav/locations/locations.component';
import { AccountClosureComponent } from './web/leftnav/Account closure/account-closure.component';
import { DeviceComponent } from './web/leftnav/DMM/device/device.component';
import { DeviceTrackingComponent } from './web/leftnav/DMM/device-tracking/device-tracking.component';
import { AssignDeviceComponent } from './web/leftnav/DMM/assign-device/assign-device.component';
import { AssignDeviceToFarmComponent } from './web/leftnav/DMM/assign-device-to-farm/assign-device-to-farm.component';
import { DeviceHealthComponent } from './web/leftnav/DMM/device-health-monitoring/device-health-monitoring.component';
import { DeviceOnHandComponent } from './web/leftnav/DMM/device-onhand/device-onhand.component';
import { SrStatusCodesComponent } from './web/leftnav/sr-status-codes/sr-status-codes.component';
import { SrTopicsComponent } from './web/leftnav/sr-topics/sr-topics.component';
import { AutoUpdateComponent } from './web/leftnav/auto-update/auto-update.component';
import { FileDownloadComponent } from './web/leftnav/file-download/file-download.component';
import { FileUploadComponent } from './web/leftnav/file-upload/file-upload.component';
import { IoPortsComponent } from './web/leftnav/io-ports/io-ports.component';
import { WifiComponent } from './web/leftnav/wifi/wifi.component';
import { DeviceParametersComponent } from './web/leftnav/device-parameters/device-parameters.component';
import { DeviceModelsComponent } from './web/leftnav/device-models/device-models.component';
import { SensorsComponent } from './web/leftnav/sensors/sensors.component';
import { SensorTypesComponent } from './web/leftnav/sensor-types/sensor-types.component';
import { ServiceRequestsComponent } from './web/leftnav/service-requests/service-requests.component';
import { QualityParameterRangesComponent } from './web/leftnav/test-configurations/quality-parameter-ranges/quality-parameter-ranges.component';
import { QualityParameterTypesComponent } from './web/leftnav/test-configurations/quality-parameter-types/quality-parameter-types.component';
import { QualityParametersComponent } from './web/leftnav/test-configurations/quality-parameters/quality-parameters.component';
import { TestCategoriesComponent } from './web/leftnav/test-configurations/test-categories/test-categories.component';
import { ContinuousMonitoringComponent } from './web/leftnav/continuous-monitoring/continuous-monitoring.component';

/**
 * @constant routes
 * @type {Routes}
 * @description
 * Defines the primary routing configuration for the AquaCloud application. This array maps URL paths
 * to their corresponding Angular components, enabling navigation throughout the application.
 *
 * **Key Features:**
 * - **Route Protection**: Utilizes the `AuthGuard` to protect routes that require user authentication.
 * - **Lazy Loading (Future)**: Structured to support lazy loading of feature modules to improve initial load times.
 * - **Role-Based Access (Implicit)**: The structure allows for different views based on user roles, managed by the components themselves or future role-based guards.
 * - **Hierarchical Routing**: Uses a nested `children` array under the `/home` path to create a consistent layout for authenticated users, with a shared `HomeComponent` containing the main navigation.
 *
 * **Route Structure:**
 * 1. **Public Routes**: Accessible to all users without authentication (e.g., `/login`, `/forgotpasswordstep1`).
 * 2. **Authenticated Routes**: Nested under `/home` and protected by `AuthGuard`. These routes represent the core application functionality.
 * 3. **Specialized Routes**: Top-level routes for specific purposes, like `smelandingpage`.
 * 4. **Wildcard Route**: A catch-all route (`**`) that redirects any unmatched URL to the login page, preventing 404 errors.
 *
 * @guard AuthGuard - Protects routes by verifying that a user session exists in `localStorage`. If the check fails, it redirects the user to the `/login` page.
 *
 * @example
 * // A typical protected route definition
 * {
 *   path: 'home',
 *   component: HomeComponent,
 *   canActivate: [AuthGuard], // This route and its children are protected
 *   children: [
 *     { path: 'dashboard', component: BlubayDashboardComponent, canActivate: [AuthGuard] }
 *   ]
 * }
 */
const routes: Routes = [
  // ==========================================================================
  // Public & Redirection Routes
  // ==========================================================================
  { path: '', pathMatch: 'full', redirectTo: '/login' },
  { path: '#/', pathMatch: 'full', redirectTo: '/login' },
  { path: 'login', component: LoginComponent },

  // ==========================================================================
  // Main Authenticated Application Layout
  // All child routes are protected by the parent's AuthGuard.
  // ==========================================================================
  {
    path: 'home', component: HomeComponent, canActivate: [AuthGuard],
    children: [
      { path: '', component: LandingpageComponent, canActivate: [AuthGuard] },
      { path: 'privacypolicy', component: PrivacyPolicyComponent },
      { path: 'termsofduse', component: TermsofuseComponent },

      // --------------------------------------------------------------------------
      // Dashboard & Details
      // --------------------------------------------------------------------------
      { path: 'historywaterquality', component: WaterQualityHistoryComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Reports
      // --------------------------------------------------------------------------
      { path: 'traceabilityreport', component: PondTracebilityComponent, canActivate: [AuthGuard] },

      

      // --------------------------------------------------------------------------
      // Admin Panel: Configuration, Masters & DMM
      // --------------------------------------------------------------------------
      { path: 'devicemapping', component: MapDeviceComponent, canActivate: [AuthGuard] },
      { path: 'addbarcode', component: BarCodeComponent, canActivate: [AuthGuard] },
      { path: 'assigndevicetofarm/:providerid/:deviceId/:deviceNumber/:isCM/:type', component: AssignDeviceToFarmComponent, canActivate: [AuthGuard] },
      { path: 'deviceModels', component: DeviceModelsComponent, canActivate: [AuthGuard] },
      { path: 'deviceParameters', component: DeviceParametersComponent, canActivate: [AuthGuard] },
      { path: 'devices', component: DeviceComponent, canActivate: [AuthGuard] },
      { path: 'assignDevice', component: AssignDeviceComponent, canActivate: [AuthGuard] },
      { path: 'assignDevicetoFarm', component: AssignDeviceToFarmComponent, canActivate: [AuthGuard] },
      { path: 'deviceHealthMonitoring', component: DeviceHealthComponent, canActivate: [AuthGuard] },
      { path: 'deviceOnHand', component: DeviceOnHandComponent, canActivate: [AuthGuard] },
      { path: 'deviceTracking', component: DeviceTrackingComponent, canActivate: [AuthGuard] },
      { path: 'autoUpdate', component: AutoUpdateComponent, canActivate: [AuthGuard] },
      { path: 'fileDownload', component: FileDownloadComponent, canActivate: [AuthGuard] },
      { path: 'fileUpload', component: FileUploadComponent, canActivate: [AuthGuard] },
      { path: 'ioPorts', component: IoPortsComponent, canActivate: [AuthGuard] },
      { path: 'wifi', component: WifiComponent, canActivate: [AuthGuard] },
      { path: 'sensors', component: SensorsComponent, canActivate: [AuthGuard] },
      { path: 'sensorTypes', component: SensorTypesComponent, canActivate: [AuthGuard] },
      { path: 'srTopics', component: SrTopicsComponent, canActivate: [AuthGuard] },
      { path: 'srStatusCodes', component: SrStatusCodesComponent, canActivate: [AuthGuard] },
      { path: 'serviceRequests', component: ServiceRequestsComponent, canActivate: [AuthGuard] },
      { path: 'qualityParameterRanges', component: QualityParameterRangesComponent, canActivate: [AuthGuard] },
      { path: 'qualityParameterTypes', component: QualityParameterTypesComponent, canActivate: [AuthGuard] },
      { path: 'qualityParameters', component: QualityParametersComponent, canActivate: [AuthGuard] },
      { path: 'testCategories', component: TestCategoriesComponent, canActivate: [AuthGuard] },
      { path: 'continuousMonitoring', component: ContinuousMonitoringComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Farm Metrics
      // --------------------------------------------------------------------------
      { path: 'farmwaterquality', component: WaterQualityComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Trading Community & Business Entity Management
      // --------------------------------------------------------------------------
      { path: 'locations', component: LocationComponent, canActivate: [AuthGuard] },
      { path: 'sections', component: SectionsComponent, canActivate: [AuthGuard] },
      { path: 'ponds', component: PondsComponent, canActivate: [AuthGuard] },
      { path: 'pondcultures', component: PondCulturesComponent, canActivate: [AuthGuard] },
      { path: 'account-closure', component: AccountClosureComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Miscellaneous & Other Features
      // --------------------------------------------------------------------------
      {
        path: 'batchentrydashboard', component: BatchEntryDashboardComponent, canActivate: [AuthGuard]
      },
      {
        path: 'analytics', component: AnalyticsComponent, canActivate: [AuthGuard]
      },

      // Wildcard route for any unknown paths under '/home'
      { path: '**', component: LoginComponent },
    ],
  },
  // ==========================================================================
  // Standalone Feature Routes
  // ==========================================================================
];

/**
 * @module AppRoutingModule
 * @description
 * The main routing module for the application.
 *
 * This module configures the Angular `RouterModule` with all the application's routes.
 * It uses the `forRoot` method because it is the root routing module.
 *
 * **Configuration:**
 * - `useHash: true`: Enables the hash-based routing strategy (`/#/path`). This is often used for compatibility with older browsers or specific server configurations.
 * - `scrollPositionRestoration: 'top'`: Ensures that the user is scrolled to the top of the page upon every navigation.
 */
@NgModule({
  // imports: [RouterModule.forRoot(routes)],
  imports: [RouterModule.forRoot(routes, { useHash: true, scrollPositionRestoration: 'top' })],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
